// A17 threads — list panel. The full thread surface (messages +
// composer) lives in the right drawer (ThreadDrawerView); this panel
// is just a list-and-create shelf that opens a chosen thread in the
// drawer when clicked.

import { useEffect, useState } from "react";
import {
  ApiError,
  createThread,
  decodeIdentity,
  extractRows,
  listThreads,
} from "../api/client";
import type { DrawerContent } from "./tabs/drawerContent";

interface Props {
  workspaceId: number;
  onOpenDrawerContent?: (content: DrawerContent) => void;
  compact?: boolean;
}

interface Thread {
  thread_id: number;
  kind: string;
  title: string;
  created_by: string;
  created_at: number;
}

export function ThreadsPanel({
  workspaceId,
  onOpenDrawerContent,
  compact = false,
}: Props) {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newKind, setNewKind] = useState<"group" | "dm">("group");

  const refreshThreads = async () => {
    try {
      const result = await listThreads(workspaceId);
      const parsed: Thread[] = extractRows(result).map((r) => ({
        thread_id: Number(r[0] ?? 0),
        kind: String(r[1] ?? ""),
        title: String(r[2] ?? ""),
        created_by: decodeIdentity(r[3]),
        created_at: Number(r[4] ?? 0),
      }));
      setThreads(parsed);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  useEffect(() => {
    void refreshThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const createNew = async () => {
    if (!newTitle.trim()) return;
    try {
      await createThread(workspaceId, newKind, newTitle.trim());
      setNewTitle("");
      setCreateOpen(false);
      await refreshThreads();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const openInDrawer = (t: Thread) => {
    if (!onOpenDrawerContent) return;
    onOpenDrawerContent({
      kind: "thread",
      threadId: t.thread_id,
      title: t.title,
      threadKind: t.kind === "dm" ? "dm" : "group",
    });
  };

  return (
    <section
      className={`flex flex-col rounded-panel border border-edge bg-surface-glass ${
        compact ? "gap-2 p-2" : "gap-3 p-4"
      }`}
      data-testid="threads-panel"
    >
      <header className="flex items-baseline justify-between gap-2">
        <div>
          <h3 className={compact ? "text-xs font-semibold text-primary" : "text-sm font-semibold text-primary"}>
            Side threads
          </h3>
          {!compact && (
            <p className="mt-1 text-xs text-muted">
              Group conversations + DMs scoped to this workspace. Click a
              thread to open it in the right drawer.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen((v) => !v)}
          className="rounded-md border border-accent bg-accent/10 px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/20"
        >
          {createOpen ? "Cancel" : compact ? "+" : "+ Thread"}
        </button>
      </header>

      {createOpen && (
        <div
          className={`flex gap-2 rounded-md border border-edge bg-surface-raised p-2 ${
            compact ? "flex-col" : "items-center"
          }`}
        >
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Thread title…"
            className="flex-1 rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary placeholder:text-faint"
          />
          <select
            value={newKind}
            onChange={(e) => setNewKind(e.target.value as "group" | "dm")}
            className="rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary"
          >
            <option value="group">Group</option>
            <option value="dm">DM</option>
          </select>
          <button
            type="button"
            onClick={() => void createNew()}
            disabled={!newTitle.trim()}
            className="rounded-md border border-accent px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
          >
            Create
          </button>
        </div>
      )}

      {threads.length === 0 ? (
        <p className="text-[0.625rem] text-faint">
          No threads yet. Create one to start a side conversation.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {threads.map((t) => (
            <li key={t.thread_id}>
              <button
                type="button"
                onClick={() => openInDrawer(t)}
                disabled={!onOpenDrawerContent}
                className="w-full rounded-md border border-edge bg-surface-raised px-2 py-1 text-left text-xs text-primary hover:border-accent disabled:opacity-50"
                data-testid={`thread-row-${t.thread_id}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span>{t.title}</span>
                  <span className="font-code text-[0.625rem] text-faint">
                    {t.kind}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="text-[0.625rem] text-danger">{error}</p>}
    </section>
  );
}
